import React from "react";
import { Button, Grid, Typography } from "@mui/material";
import { Field, Form, Formik } from "formik";
import { TextField } from "formik-mui";
import { useTranslation } from "react-i18next";

import { APIError, LineBasedGrid } from "src/types";
import { ButtonGrid } from "src/components/form-components/ButtonGrid";
import { ButtonLoading } from "src/components/ButtonLoading";
import { minMaxToString } from "src/utils/string.utils";
import { LineBasedGridMutationData } from "src/hooks/grids";
import { ServerErrors } from "src/components/form-components/ServerErrors";
import { adornments } from "src/config/adornments";
import { useLineBasedGridSchema } from "src/schemas/line-based-grid.schema";

interface Props {
  data: LineBasedGrid;
  readOnly?: boolean;
  isLoading: boolean;
  errors?: APIError[] | null;
  onClose: () => void;
  onSubmit: (values: LineBasedGridMutationData) => void;
}

export function LineBasedGridForm(props: Props) {
  const { t } = useTranslation();
  const validationSchema = useLineBasedGridSchema(props.data);

  const initialValues: LineBasedGridMutationData = {
    distance: props.data.distance.value,
    count: props.data.count.value,
    minDistance: props.data.minDistance.value,
    maxDistance: props.data.maxDistance.value,
    tolerance: props.data.tolerance.value,
  };

  return render();

  function render() {
    return (
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values: LineBasedGridMutationData) => {
          return props.onSubmit(values);
        }}
      >
        {({ isSubmitting }) => (
          <Form>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography>{t("label.lineBasedGrid")}</Typography>
              </Grid>

              <Grid item xs={12}>
                <Field
                  component={TextField}
                  name="distance"
                  label={t("label.distanceBetweenPerpendicularLines")}
                  helperText={minMaxToString(props.data.distance.minMaxDefault)}
                  InputProps={adornments(t).meter}
                  type="number"
                  variant="outlined"
                  fullWidth
                  disabled={props.readOnly}
                />
              </Grid>

              <Grid item xs={12}>
                <Field
                  component={TextField}
                  name="count"
                  label={t("label.numberOfPointsOnPerpendicularLine")}
                  helperText={minMaxToString(props.data.count.minMaxDefault)}
                  type="number"
                  variant="outlined"
                  fullWidth
                  disabled={props.readOnly}
                />
              </Grid>

              <Grid item xs={12}>
                <Field
                  component={TextField}
                  name="minDistance"
                  label={t("label.distanceOfFirstPoint")}
                  helperText={minMaxToString(props.data.minDistance.minMaxDefault)}
                  InputProps={adornments(t).meter}
                  type="number"
                  variant="outlined"
                  fullWidth
                  disabled={props.readOnly}
                />
              </Grid>

              <Grid item xs={12}>
                <Field
                  component={TextField}
                  name="maxDistance"
                  label={t("label.distanceOfLastPoint")}
                  InputProps={adornments(t).meter}
                  helperText={minMaxToString(props.data.maxDistance.minMaxDefault)}
                  type="number"
                  variant="outlined"
                  fullWidth
                  disabled={props.readOnly}
                />
              </Grid>

              <Grid item xs={12}>
                <Field
                  component={TextField}
                  name="tolerance"
                  label={t("label.tolerance")}
                  helperText={minMaxToString(props.data.tolerance.minMaxDefault)}
                  InputProps={adornments(t).meter}
                  type="number"
                  variant="outlined"
                  fullWidth
                  disabled={props.readOnly}
                />
              </Grid>

              {props.errors && (
                <Grid item xs={12}>
                  <ServerErrors data={props.errors} />
                </Grid>
              )}

              {!props.readOnly && (
                <ButtonGrid container item xs={12} sx={{ justifyContent: "flex-end" }}>
                  <Button variant="text" onClick={props.onClose}>
                    {t("label.cancel")}
                  </Button>
                  <ButtonLoading
                    isLoading={isSubmitting || props.isLoading}
                    type="submit"
                    color="primary"
                    variant="contained"
                  >
                    {t("label.save")}
                  </ButtonLoading>
                </ButtonGrid>
              )}
            </Grid>
          </Form>
        )}
      </Formik>
    );
  }
}
